import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Eyebrow, FadeUp, Marquee, MaskedLine } from "@/components/motion";

const KNOW = [
  {
    n: "01",
    suit: "♠",
    title: "Curated",
    body: "A considered mix of men and preferences — not a beauty contest.",
  },
  {
    n: "02",
    suit: "♦",
    title: "No pressure",
    body: "Play, watch, talk or change your mind. Nothing beyond entry is compulsory.",
  },
  {
    n: "03",
    suit: "♣",
    title: "Discreet",
    body: "A private event, no photography and respect for everyone's privacy.",
  },
];

const ROUNDS = [
  {
    suit: "♠",
    round: "Round one",
    title: "Take your seat",
    body: "Drinks, names, conversation. Everyone arrives together and settles in before anything else.",
    img: "/images/round-one.png",
  },
  {
    suit: "♥",
    round: "Round two",
    title: "Show your hand",
    body: "Phones away, clothes off — by individual choice. A relaxed, body-positive room without judgement.",
    img: "/images/round-two.png",
  },
  {
    suit: "♦",
    round: "Round three",
    title: "Play when you're ready",
    body: "The playspaces open. Join in, watch, keep talking, or simply enjoy the room. Your call, always.",
    img: "/images/round-three.png",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
          <img
            src="/images/home-hero.png"
            alt="Warmly lit private lounge with men relaxing over cocktails"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 sm:px-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
          >
            <Eyebrow>Sydney · 18+ · Private events</Eyebrow>
          </motion.div>
          <h1 className="mt-4 font-serif text-6xl leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.2}>Players</MaskedLine>
            <MaskedLine delay={0.35} className="italic text-brass">
              Lounge
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-5 text-sm tracking-[0.18em] text-ivory/80 sm:text-base"
            data-testid="hero-tagline"
          >
            ♠ Play Freely. <span className="text-brass">♥</span> Play Hard.{" "}
            <span className="text-brass">♦</span> Play Safe.{" "}
            <span className="text-brass">♣</span> Play Again.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-3 max-w-md text-base text-ivory/65"
          >
            Private, curated gatherings for men in Sydney.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <Link
              to="/deal-me-in"
              data-testid="hero-deal-me-in-button"
              className="inline-flex h-13 items-center justify-center rounded-xl bg-burgundy px-8 py-3.5 text-base font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_30px_rgba(140,46,59,0.5)]"
            >
              Deal Me In
            </Link>
            <Link
              to="/the-game"
              data-testid="hero-how-it-works-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-ivory/80 transition-colors hover:text-brass"
            >
              See how the game works
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* NOT YOUR USUAL PLAY PARTY */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <FadeUp>
          <Eyebrow>01 — The difference</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-5xl">
            Not your usual <span className="italic text-brass">play party.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.12}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ivory/75">
            Everyone starts the night together. Have a drink. Learn a few names. Catch up with
            mates. Find your feet. Notice whose eye you are catching.
          </p>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ivory/75">
            There's no rush, no attitude and no expectation that you need to do anything at all.
            You decide how you play your hand.
          </p>
          <Link
            to="/the-game"
            data-testid="home-how-game-works-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-colors hover:text-ivory"
          >
            How the game works
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>

      {/* THREE THINGS TO KNOW */}
      <section className="border-y border-brass/10 bg-charcoal/40">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <FadeUp>
            <Eyebrow>02 — Before anything else</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl text-ivory sm:text-5xl">Three things to know</h2>
          </FadeUp>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {KNOW.map((item, i) => (
              <FadeUp key={item.n} delay={i * 0.1}>
                <article
                  data-testid={`know-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group h-full rounded-2xl border border-brass/15 bg-ink/70 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brass/40"
                >
                  <div className="flex items-center justify-between">
                    <span aria-hidden="true" className="text-xl text-brass">
                      {item.suit}
                    </span>
                    <span className="font-serif text-sm italic text-ivory/30">{item.n}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-ivory">{item.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-ivory/65">{item.body}</p>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* THREE ROUNDS PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <FadeUp>
          <Eyebrow>03 — The shape of the night</Eyebrow>
          <h2 className="mt-4 max-w-xl font-serif text-3xl leading-tight text-ivory sm:text-5xl">
            One room. One group. <span className="italic text-brass">Three rounds.</span>
          </h2>
          <p className="mt-6 max-w-xl text-base text-ivory/65">
            The evening changes gear gradually — and every step forward is a personal choice.
          </p>
        </FadeUp>
        <div className="mt-14 space-y-16 sm:space-y-24">
          {ROUNDS.map((r, i) => (
            <FadeUp key={r.round} delay={0.05}>
              <div
                className={`grid items-center gap-8 md:grid-cols-2 ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="overflow-hidden rounded-2xl border border-brass/15">
                  <motion.img
                    src={r.img}
                    alt=""
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                  />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
                    <span aria-hidden="true" className="mr-2">{r.suit}</span>
                    {r.round}
                  </p>
                  <h3 className="mt-3 font-serif text-3xl text-ivory sm:text-4xl">{r.title}</h3>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-ivory/70">{r.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* FIRST TIME BAND */}
      <section className="bg-ivory text-ink">
        <div className="mx-auto max-w-7xl px-5 py-18 sm:px-8 sm:py-24">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-burgundy">
              <span aria-hidden="true" className="mr-2">♥</span>New here?
            </p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight sm:text-5xl">
              First time? <span className="italic">Curious is enough.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/75">
              You don't need to arrive with a plan. The evening is designed to give you time to
              settle in before deciding what, if anything, you want to do.
            </p>
            <Link
              to="/first-game"
              data-testid="home-first-game-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-burgundy transition-colors hover:text-ink"
            >
              Read the first-timer's guide
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ATMOSPHERE PANEL */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <FadeUp>
          <div className="relative overflow-hidden rounded-2xl border border-brass/15">
            <img
              src="/assets/campaign-july-hero.webp"
              alt="Players Lounge atmosphere — illustrated lounge scene from a past campaign"
              loading="lazy"
              className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 sm:p-10">
              <p className="text-[11px] uppercase tracking-[0.3em] text-brass">Atmosphere</p>
              <p className="mt-2 max-w-md font-serif text-2xl italic text-ivory sm:text-3xl">
                A room where everybody got the memo: relax.
              </p>
              <p className="mt-2 text-xs text-ivory/50">
                Players Lounge campaign artwork — not a current event.
              </p>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-brass/10 bg-charcoal/40">
        <div className="mx-auto max-w-7xl px-5 py-20 text-left sm:px-8 sm:py-28">
          <FadeUp>
            <Eyebrow suit="♦">The next step</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-tight text-ivory sm:text-6xl">
              The seat is there <span className="italic text-brass">when you're ready.</span>
            </h2>
            <p className="mt-6 max-w-xl text-lg text-ivory/70">
              Join the Players List. No obligation to attend — you'll simply hear about upcoming
              gatherings first.
            </p>
            <Link
              to="/deal-me-in"
              data-testid="home-final-deal-me-in-button"
              className="mt-9 inline-flex h-13 items-center rounded-xl bg-burgundy px-9 py-3.5 text-base font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_30px_rgba(140,46,59,0.5)]"
            >
              Deal Me In
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Home;
